import CampusSelect from "components/CampusSelect"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import {KlassappTableHOC} from "HOC"
import {isEmpty} from "lodash"
import {useCallback, useEffect, useState} from "react"
import {majorVersionsService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import {Major} from "types/major"
import {useQuery} from "@tanstack/react-query"
import {Col, Row} from "antd"
import {Campus} from "types/campus"
import {useModel} from "hooks"
import {FilterKey} from "types/filter"
import styles from "./ProgramTable.module.css"

const statusOptions = [
    {value: null, label: "All"},
    {value: true, label: "Active"},
    {value: false, label: "Inactive"}
]

const FILTER_KEY = FilterKey.ClockHoursTranscriptProgram

type ProgramFilter = {
    campuses: Campus.Campus[]
    isActive: boolean | null
}

const INITIAL_FILTER: ProgramFilter = {
    campuses: [],
    isActive: true
}

const restoreFilter = (storageData: {filter?: Partial<ProgramFilter>} | null): ProgramFilter => {
    const storedFilter = storageData?.filter
    if (!storedFilter || isEmpty(storedFilter)) {
        return INITIAL_FILTER
    }
    return {
        campuses: storedFilter.campuses ?? [],
        isActive: storedFilter.isActive !== undefined ? storedFilter.isActive : INITIAL_FILTER.isActive
    }
}

type ProgramTableProps = KlassappTableProps & {
    setSelectedProgram: (program: Major.MajorVersion) => void
}

const ProgramTable = (props: ProgramTableProps) => {
    const {
        dispatch,
        dispatchFunc,
        setSelectedProgram,
        page,
        pageSize,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        columns,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        onChangePage
    } = props
    const model = useModel()
    const storageData = model.getStorageFilter(FILTER_KEY)
    const restoredSearch = !isEmpty(storageData) && typeof storageData.search === "string" ? storageData.search : ""
    const restoredFilter = restoreFilter(storageData)
    const [search, setSearch] = useState(restoredSearch)
    const [filter, setFilter] = useState(restoredFilter)
    const [appliedFilter, setAppliedFilter] = useState(restoredFilter)

    const campusIds = appliedFilter.campuses?.length ? appliedFilter.campuses.map((campus) => campus.id) : undefined
    const isActive = appliedFilter.isActive

    const {data: programVersions, isFetching} = useQuery(
        ["list-program-versions", page, pageSize, search, orderField, campusIds, isActive],
        async () => {
            const response = await majorVersionsService.getAll({
                range: {page, pageSize: pageSize},
                linkedEntities: true,
                filter: {
                    search,
                    type: Major.Unit.Clock,
                    campusIds,
                    ...(isActive !== null ? {isActive} : {})
                },
                sort: {orderBy: orderField.field ?? "id", orderDir: orderField.order ?? "DESC"}
            })
            if (response.data) {
                response.data = response.data.map((item) => {
                    return {
                        ...item,
                        programName: item.program.name,
                        programCode: item.program.code
                    }
                })
            }
            return response
        },
        {
            enabled: !!isLoadedTableFuncs,
            initialData: {
                data: [],
                total: 0
            }
        }
    )

    const renderStatus = (status) => {
        return <p className={`status-${status} ${styles.status}`}>{status}</p>
    }

    const getFields = () => {
        return ["Program Name", "Program Code", "Program Version Code", "Program Version Name", "Campuses", "Status"]
    }

    const getPageTitle = () => {
        return "Major"
    }

    const getColumns = () => {
        return [
            {
                title: "Program Name",
                field: "programName",
                columnIndex: 1
            },
            {
                title: "Program Code",
                field: "programCode",
                columnIndex: 2
            },
            {
                title: "Program Version Code",
                field: "version_code",
                orderField: "versionCode",
                sortable: true,
                columnIndex: 3
            },
            {
                title: "Program Version Name",
                field: "version_name",
                orderField: "versionName",
                sortable: true,
                columnIndex: 4
            },
            {
                title: "Campuses",
                field: "campuses",
                columnIndex: 5,
                render: (campuses) => campuses.map((campus) => campus.name).join(", ")
            },
            {
                title: "Status",
                field: "isActive",
                columnIndex: 6,
                render: (isActive) => renderStatus(isActive ? "active" : "inactive")
            }
        ]
    }

    const getFilterMemoryKey = () => {
        return FILTER_KEY
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programVersions])

    const onClickRowItem = (row) => {
        setSelectedProgram(row)
    }

    const onChangeFilter = useCallback(<K extends keyof typeof filter>(key: K, value: typeof filter[K]) => {
        setFilter((prev) => ({...prev, [key]: value}))
    }, [])

    const onClearFilter = useCallback(() => {
        setFilter(INITIAL_FILTER)
        setAppliedFilter(INITIAL_FILTER)
        model.updateStorageFilter(FILTER_KEY, {filter: INITIAL_FILTER, page: 1})
        onChangePage(1)
    }, [model, onChangePage])

    const onApplyFilter = useCallback(() => {
        setAppliedFilter(filter)
        model.updateStorageFilter(FILTER_KEY, {filter, page: 1})
        onChangePage(1)
    }, [filter, model, onChangePage])

    const onSearchInput = useCallback(
        (value = "") => {
            setSearch(value)
            model.updateStorageFilter(FILTER_KEY, {search: value, page: 1})
            onChangePage(1)
        },
        [model, onChangePage]
    )

    return (
        <>
            <BaseNewFilter
                filter={filter}
                searchValue={search}
                onSearchInput={onSearchInput}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}>
                <Row gutter={[40, 32]}>
                    <Col span={12}>
                        <FormLabel label="Campus" />
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder="Campus"
                            value={filter.campuses}
                            onChange={(newValue) => onChangeFilter("campuses", newValue ?? [])}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Status" />
                        <KlassDropdown
                            valueKey="value"
                            labelKey="label"
                            placeholder="Status"
                            isClearable
                            value={statusOptions.find((opt) => opt.value === filter.isActive)}
                            options={statusOptions}
                            onChange={(newValue) => onChangeFilter("isActive", newValue?.value ?? null)}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={programVersions.total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={programVersions.data}
                menuActions={[]}
                isLoading={isFetching}
                allFields={allFields}
                isShowCheckedColumn={false}
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onUpdateRowData={props.onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
            />
        </>
    )
}

export default KlassappTableHOC(ProgramTable)
